import { Controller } from "@hotwired/stimulus"
import { parseSettingsParams } from "../helpers/parse_settings"

// Owns the live-preview panel lifecycle:
//  - sample selector change reloads the preview turbo frame at the new src
//  - a manual "Run" button forces an immediate reload
//  - the status chip transitions running -> ran-at on turbo:frame-load
//
// The preview renders the last SAVED columns and mappings: the editor saves
// through one explicit save button, whose full-page render reloads this frame,
// and the Run button re-runs it on demand in between. The FILE SETTINGS are the
// exception — every reload carries the ones the screen currently shows, so a
// delimiter typed but not yet saved is what the sample is read with. Without
// formIdValue — the read-only detail's sample panel — nothing is carried and
// the saved settings stay in charge.
export default class extends Controller {
  static targets = ["frame", "status"]
  static values = { statusRunning: String, formId: String }

  // Which iteration card the operator was last working in. The preview re-runs
  // against the SAVED configuration, so this says nothing about the result — only
  // which block the answer they are looking for is in.
  noteIteration(event) {
    const card = event.target.closest("[data-iteration-rank]")
    if (card) this.focusRank = card.dataset.iterationRank
  }

  // Reload the frame at a new sample. The <select> carries the per-option
  // preview src in data-preview-src so we don't rebuild query strings here.
  selectSample(event) {
    this.focusRank = null
    const src = event.target.selectedOptions[0]?.dataset.previewSrc
    if (src) this.reloadAt(src)
  }

  run() {
    if (this.hasFrameTarget) this.reloadAt(this.frameTarget.src)
  }

  reloadAt(src) {
    if (!this.hasFrameTarget || !src) return

    this.markRunning()
    this.frameTarget.classList.add("opacity-50", "pointer-events-none")
    this.frameTarget.setAttribute("src", src)
    // An explicit re-run must fetch now, even when the preview pane is scrolled
    // out of view. A `loading="lazy"` frame gates every src change behind an
    // IntersectionObserver, so an off-screen re-run would never fire (on a
    // stacked/narrow layout the pane can sit below the fold indefinitely).
    // Promoting to eager stops that observer and loads the current src at once;
    // reload() then forces the fetch for an unchanged src (the Run button) too.
    // Turbo aborts any in-flight request for this frame, so the latest selection
    // always wins — a slower earlier render can't land late.
    this.frameTarget.setAttribute("loading", "eager")
    this.frameTarget.reload()
  }

  // Wired on the frame itself, so EVERY load carries the settings on screen —
  // the first, lazy one included. That one matters: the pane sits below the fold
  // on a narrow layout, so it can first run long after a setting was typed, and
  // a stale result there looks like an ordinary one. Turbo hands us the very URL
  // it is about to fetch, so the frame's src attribute stays clean and an
  // already-loaded frame is not re-run by this — only the Run button and the
  // sample selector re-run it.
  //
  // The settings are rewritten rather than merged: the URL is often the frame's
  // own previous src, which already carries the values of an earlier run.
  addParseSettings(event) {
    const url = event.detail && event.detail.url
    if (!url) return

    // Cleared here and re-set, never merged: the URL is often the frame's own
    // previous src and would otherwise re-open a block on an unrelated run. The
    // note itself is dropped in loaded(), not here — one Run issues several
    // fetches (src change, eager promotion, reload) and the LAST one renders, so
    // consuming it on the first would leave the winner without it.
    url.searchParams.delete("focus_iteration")
    if (this.focusRank) url.searchParams.set("focus_iteration", this.focusRank)

    const params = parseSettingsParams(this.formIdValue)
    if (!Array.from(params.keys()).length) return

    Array.from(url.searchParams.keys())
      .filter((key) => key.startsWith("file_layout_draft["))
      .forEach((key) => url.searchParams.delete(key))
    params.forEach((value, key) => url.searchParams.set(key, value))
  }

  // Wired via data-action="turbo:frame-load->preview-refresh#loaded" on the
  // frame; the freshly-rendered chip already carries its ran-at label, so we
  // just clear any transient running styling on the wrapper.
  loaded() {
    this.focusRank = null
    this.element.removeAttribute("data-preview-state")
    if (this.hasFrameTarget) {
      this.frameTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  markRunning() {
    this.element.setAttribute("data-preview-state", "running")
    if (this.hasStatusTarget && this.hasStatusRunningValue) {
      this.statusTarget.textContent = this.statusRunningValue
    }
  }
}
